import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UiPreferenceValues, useBackofficeUiPreferences } from 'src/shared/backofficeUiPreferences';
import type { ElementSize } from 'src/shared/hooks/useElementSize';

export type PropertiesPanelDock = 'right' | 'side';
export type WorkspaceLayoutMode = 'workspace' | 'drawer';
export type SectionRails = 'compact' | 'expanded';
export type ColorScheme = 'system' | 'light' | 'dark';
export type PanelKey = 'left' | 'right';
export type MeasurementUnit = 'm' | 'cm' | 'mm' | 'in';
export type SelectionHighlightSetting = 'on' | 'off';
export type SingleKeyShortcutsSetting = 'on' | 'off';

export interface WorkspacePreferences {
    colorScheme: ColorScheme;
    layoutMode: WorkspaceLayoutMode;
    propertiesPanel: PropertiesPanelDock;
    sectionRails: SectionRails;
    measurementUnit: MeasurementUnit;
    selectionHighlight: SelectionHighlightSetting;
    highlightColor: string;
    singleKeyShortcuts: SingleKeyShortcutsSetting;
    collapsed: PanelKey[];
    sizes: Record<PanelKey, number>;
}

// Le scelte di chi lavora. Il resto di `WorkspacePreferences` è geometria (`sizes`,
// `collapsed`) o non è una scelta di nessuno (`layoutMode`).
export type WorkspacePreferenceKey =
    'colorScheme' | 'propertiesPanel' | 'sectionRails' | 'measurementUnit' | 'selectionHighlight' | 'highlightColor' | 'singleKeyShortcuts';
export type WorkspacePreferencesSnapshot = Partial<Record<WorkspacePreferenceKey, string>>;

// Le preferenze stanno sul server, nel documento di `src/shared/backofficeUiPreferences.tsx`, sotto
// questo scope: le sei scelte e le larghezze delle colonne. Contratto:
// docs/preferenze-interfaccia-microservizio.md.
const SCOPE = 'scene-editor';

// In locale resta solo `collapsed`: quali pannelli sono chiusi è uno stato del momento, come la
// sidebar. È la chiave che prima teneva tutto, ed è quindi anche quella da cui si migra.
const LOCAL_KEY = 'zakeke.sceneEditor.workspace';

// I default di fabbrica sono quelli del prototipo: le proprietà accanto alla lista, e i
// rail che scrivono i nomi. Non sono una preferenza di gusto — sono la schermata che
// chiunque apra per la prima volta deve vedere, ed è quella su cui il disegno è stato
// giudicato. L'altro attracco resta a un clic, in Settings → Interface.
const DEFAULT_PREFERENCES: WorkspacePreferences = {
    colorScheme: 'system',
    layoutMode: 'workspace',
    propertiesPanel: 'side',
    sectionRails: 'expanded',
    measurementUnit: 'm',
    selectionHighlight: 'on',
    // Il colore con cui l'editor nasce in `load3DScene`.
    highlightColor: '#FF5500',
    // LE SCORCIATOIE A UN TASTO SI POSSONO SPEGNERE (utente, 2026-10-22, WCAG 2.1.4): chi detta a voce o usa uno screen
    // reader le fa partire senza volerlo. Accese di partenza, così per chi le usa non cambia niente.
    singleKeyShortcuts: 'on',
    collapsed: [],
    // L'INSPECTOR NASCE LARGO 480 (utente, 2026-10-15): è la misura in cui le tab di un'opzione del Composer
    // — Actions, Settings, Links, Retarget, Prices, Properties — stanno tutte in vista in inglese e in
    // francese (misurate: 462 e 466 px). In italiano, spagnolo, portoghese e tedesco servono 520–620 px:
    // la fila scorre di lato con la sfumatura (`ComposerTabs`), e chi vuole allarga la colonna fino a 540.
    // E LA PRIMA COLONNA NASCE UGUALE, rail compreso (utente, 2026-10-22): due colonne dello stesso peso
    // lasciano la scena al centro.
    sizes: { left: 480, right: 480 }
};

// UNA SCELTA SI CONTROLLA CONTRO IL SUO ELENCO, un colore contro la sua forma: gli esadecimali
// possibili sono sedici milioni e mezzo, e l'elenco non si scrive.
type ListPreferenceKey = Exclude<WorkspacePreferenceKey, 'highlightColor'>;

const ALLOWED_VALUES: Record<ListPreferenceKey, readonly string[]> = {
    colorScheme: ['system', 'light', 'dark'],
    propertiesPanel: ['right', 'side'],
    sectionRails: ['compact', 'expanded'],
    measurementUnit: ['m', 'cm', 'mm', 'in'],
    selectionHighlight: ['on', 'off'],
    singleKeyShortcuts: ['on', 'off']
};
const HEX = /^#[0-9a-fA-F]{6}$/;

const LIST_PREFERENCE_KEYS = Object.keys(ALLOWED_VALUES) as ListPreferenceKey[];

const PANELS: PanelKey[] = ['left', 'right'];

// LA LARGHEZZA È DEL POSTO, NON DELLA SEZIONE: si salva «quanto è larga la prima colonna», non
// «quanto è largo l'Outliner», e la seconda colonna conta il suo posto allo stesso modo.
const SIZE_KEYS: Record<PanelKey, string> = { left: 'panel1', right: 'panel2' };

// I DUE NUMERI SONO DELLA COLONNA, RAIL COMPRESO (utente, 2026-10-22): prima il rail si aggiungeva ai due
// capi della prima colonna, che così pesava sempre settanta pixel più dell'Inspector e spostava la scena
// di lato. Ora rail e outliner insieme hanno gli stessi limiti dell'Inspector: al minimo l'outliner ha
// 280 px di contenuto coi nomi nel rail, 307 col rail compatto.
const MIN_CONTENT = 350;
// 540 e non 500 (utente, 2026-10-15): è quanto serve perché le tab di un'opzione del Composer stiano
// tutte in vista anche in italiano (~520 px). Il default resta 480: chi lavora in una lingua più lunga
// allarga la colonna se vuole.
const MAX_CONTENT = 540;

// Oltre il 2K lo spazio smette di essere il vincolo: cento pixel in più a chi legge una lista lunga
// non li toglie a nessuno. Sotto, restano cinquecento.
const WIDE_WORKSPACE = 2048;
const MAX_CONTENT_WIDE = 600;

const RAIL_WIDTHS: Record<SectionRails, number> = { compact: 43, expanded: 70 };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// Il massimo di una colonna nel workspace di adesso: le due colonne ne hanno uno solo.
const getPanelMaximum = (workspace: ElementSize | null) => ((workspace?.width ?? 0) > WIDE_WORKSPACE ? MAX_CONTENT_WIDE : MAX_CONTENT);

// OGNI COLONNA NASCE DALLA SUA MISURA (utente, 2026-10-15). Prima l'Inspector nasceva largo come la
// prima colonna; ora ha un default suo, quello in cui le tab di un'opzione stanno in vista (vedi
// `DEFAULT_PREFERENCES.sizes`). I due ambienti restano uguali fra loro: usano questo stesso hook.
// Il default della superficie, riportato dentro i limiti: su un workspace stretto il massimo può stare sotto.
const getDefaultSize = (panel: PanelKey, workspace: ElementSize | null) => clamp(DEFAULT_PREFERENCES.sizes[panel], MIN_CONTENT, getPanelMaximum(workspace));

// IL NUMERO SALVATO È UNA FRAZIONE DEL WORKSPACE, non un conto di pixel: un px misurato su un 27"
// su un portatile non vuol dire niente, una frazione vuol dire ancora quello che voleva dire.
// Quattro decimali: su 2000 px l'errore di riconversione sta sotto il mezzo pixel.
const toFraction = (size: number, workspace: ElementSize | null) => {
    const extent = workspace?.width ?? 0;
    return extent > 0 ? (size / extent).toFixed(4) : undefined;
};

// E SI RIVALIDA A OGNI LETTURA, non si applica: i limiti dipendono dalla larghezza dello schermo, e un
// valore salvato altrove può essere fuori scala qui.
const resolveSize = (panel: PanelKey, stored: string | undefined, workspace: ElementSize | null) => {
    const fraction = Number(stored);
    const extent = workspace?.width ?? 0;
    if (stored === undefined || extent === 0 || !(fraction > 0 && fraction < 1)) return getDefaultSize(panel, workspace);
    return clamp(fraction * extent, MIN_CONTENT, getPanelMaximum(workspace));
};

// Il confronto è con TUTTI i valori ammessi, non solo con quello diverso dal default:
// scritto all'incontrario, cambiare il default riscriverebbe la scelta di chi aveva
// preso l'altra.
const pick = <K extends ListPreferenceKey>(key: K, value: string | undefined): WorkspacePreferences[K] =>
    (value !== undefined && ALLOWED_VALUES[key].includes(value) ? value : DEFAULT_PREFERENCES[key]) as WorkspacePreferences[K];

const pickColor = (value: string | undefined) => (value !== undefined && HEX.test(value) ? value : DEFAULT_PREFERENCES.highlightColor);

const resolvePreferences = (stored: UiPreferenceValues, collapsed: PanelKey[], workspace: ElementSize | null): WorkspacePreferences => {
    const sectionRails = pick('sectionRails', stored.sectionRails);

    return {
        colorScheme: pick('colorScheme', stored.colorScheme),
        // L'Inspector a cassetto non è una scelta offerta: lo step 5 lo vuole colonna, e nel
        // prototipo il cassetto non esiste. Per questo non si salva nemmeno.
        layoutMode: 'workspace',
        propertiesPanel: pick('propertiesPanel', stored.propertiesPanel),
        sectionRails,
        measurementUnit: pick('measurementUnit', stored.measurementUnit),
        selectionHighlight: pick('selectionHighlight', stored.selectionHighlight),
        highlightColor: pickColor(stored.highlightColor),
        singleKeyShortcuts: pick('singleKeyShortcuts', stored.singleKeyShortcuts),
        collapsed,
        sizes: {
            left: resolveSize('left', stored[SIZE_KEYS.left], workspace),
            right: resolveSize('right', stored[SIZE_KEYS.right], workspace)
        }
    };
};

// Ciò che sta nella chiave locale. Finché la migrazione non è avvenuta ci sono ancora le voci di
// quando la chiave teneva tutto — le larghezze in pixel.
type LocalPreferences = Partial<Omit<WorkspacePreferences, 'sizes'>> & { sizes?: Partial<Record<PanelKey, number>> };

const readLocal = (): LocalPreferences => {
    if (typeof window === 'undefined') return {};

    try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(LOCAL_KEY) ?? '{}');
        return typeof stored === 'object' && stored !== null ? (stored as LocalPreferences) : {};
    } catch {
        return {};
    }
};

const writeLocal = (value: LocalPreferences) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(LOCAL_KEY, JSON.stringify(value));
};

const readCollapsed = (local: LocalPreferences): PanelKey[] =>
    Array.isArray(local.collapsed) ? local.collapsed.filter((panel): panel is PanelKey => PANELS.includes(panel)) : [];

const readLegacyPreferences = (local: LocalPreferences) => {
    const entries: WorkspacePreferencesSnapshot = {};
    for (const key of LIST_PREFERENCE_KEYS) {
        const value = local[key];
        if (typeof value === 'string' && ALLOWED_VALUES[key].includes(value)) entries[key] = value;
    }
    return entries;
};

// L'ALONE DELLA SELEZIONE AVEVA UNA CHIAVE SUA (2026-09-15), perché il servizio non lo teneva
// ancora. Ora lo tiene, e quella chiave serve un'ultima volta: si porta su e si butta.
const HIGHLIGHT_LOCAL_KEY = 'zakeke.sceneEditor.selectionHighlight';

const readLegacyHighlight = (): WorkspacePreferencesSnapshot => {
    if (typeof window === 'undefined') return {};

    try {
        const stored = JSON.parse(window.localStorage.getItem(HIGHLIGHT_LOCAL_KEY) ?? 'null') as { enabled?: unknown; color?: unknown } | null;
        if (!stored || typeof stored !== 'object') return {};

        const entries: WorkspacePreferencesSnapshot = {};
        if (typeof stored.enabled === 'boolean') entries.selectionHighlight = stored.enabled ? 'on' : 'off';
        if (typeof stored.color === 'string' && HEX.test(stored.color)) entries.highlightColor = stored.color;
        return entries;
    } catch {
        return {};
    }
};

const dropLegacyHighlight = () => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(HIGHLIGHT_LOCAL_KEY);
};

export const useWorkspacePreferences = (workspace: ElementSize | null) => {
    const { values: stored, isStored, save, fill } = useBackofficeUiPreferences(SCOPE);
    const [collapsed, setCollapsed] = useState(() => readCollapsed(readLocal()));
    const preferences = useMemo(() => resolvePreferences(stored, collapsed, workspace), [stored, collapsed, workspace]);

    // LA MIGRAZIONE: ciò che questo browser si ricordava prima che le preferenze avessero un posto sul
    // server. Riempie i buchi e non corregge niente — se il server una scelta ce l'ha già, fatta qui o
    // su un altro computer, vince lui. La copia locale si butta solo quando il server ha confermato di
    // avere questa superficie: prima, una scrittura fallita se la porterebbe via.
    //
    // Prima del disegno, perché chi aveva scelto il tema scuro non veda un lampo chiaro. Le larghezze
    // erano pixel e diventano frazioni, quindi aspettano la prima misura del workspace.
    // `undefined`: la chiave locale non è ancora stata letta; `null`: non c'è niente da portare.
    const legacySizes = useRef<{ sizes: NonNullable<LocalPreferences['sizes']> } | null | undefined>(undefined);

    useLayoutEffect(() => {
        const local = readLocal();
        const entries = { ...readLegacyPreferences(local), ...readLegacyHighlight() };
        if (Object.keys(entries).length === 0 && !local.sizes) return;
        if (legacySizes.current === undefined) {
            legacySizes.current = local.sizes ? { sizes: local.sizes } : null;
            fill(entries);
        }
        if (isStored) {
            writeLocal({ collapsed: readCollapsed(local) });
            dropLegacyHighlight();
        }
    }, [fill, isStored]);

    useLayoutEffect(() => {
        const legacy = legacySizes.current;
        if (!legacy || !workspace) return;
        legacySizes.current = null;

        // I pixel di allora si riportano dentro i limiti di ADESSO: quelli di allora non sono
        // scritti da nessuna parte, e una misura fuori scala si vedrebbe comunque riportata dentro
        // alla prima lettura.
        const entries: Record<string, string> = {};
        for (const panel of PANELS) {
            const size = Number(legacy.sizes[panel]);
            if (!size || size === getDefaultSize(panel, workspace)) continue;
            const inRange = clamp(size, MIN_CONTENT, getPanelMaximum(workspace));
            const fraction = toFraction(inRange, workspace);
            if (fraction) entries[SIZE_KEYS[panel]] = fraction;
        }
        fill(entries);
    }, [fill, workspace]);

    const setPreference = useCallback(<K extends WorkspacePreferenceKey>(key: K, value: WorkspacePreferences[K]) => save({ [key]: value }), [save]);

    // Si scrive al rilascio della presa, non a ogni movimento. E chi non è stato trascinato non si
    // scrive: tornare al default — il doppio clic sulla presa — toglie la voce, e il posto torna a
    // valere il default della superficie.
    const setPanelSize = useCallback(
        (panel: PanelKey, size: number) => {
            const isDefault = size === getDefaultSize(panel, workspace);
            const fraction = isDefault ? null : toFraction(size, workspace);
            if (fraction !== undefined) save({ [SIZE_KEYS[panel]]: fraction });
        },
        [save, workspace]
    );

    // RIMETTERE LE MISURE COM'ERANO È TOGLIERLE, non riscriverle col numero del default: una misura
    // scritta per esteso sembrerebbe una scelta, e il giorno che il default cambia resterebbe
    // indietro. È il gesto del doppio clic su una presa, per tutt'e due le colonne insieme.
    const resetPanelSizes = useCallback(() => save(Object.fromEntries(PANELS.map((panel) => [SIZE_KEYS[panel], null]))), [save]);

    // I LIMITI SONO DI QUESTA SCHERMATA, non di una costante: dipendono da quanto è largo lo schermo, e
    // qui c'è. Chi disegna le prese li riceve già fatti.
    const limits = useMemo(
        () =>
            Object.fromEntries(
                PANELS.map((panel) => [
                    panel,
                    {
                        min: MIN_CONTENT,
                        max: getPanelMaximum(workspace)
                    }
                ])
            ) as Record<PanelKey, { min: number; max: number }>,
        [workspace]
    );

    // E LA MISURA DI PARTENZA ALLO STESSO MODO: è quella a cui torna il doppio clic sulla presa, e
    // scritta a mano accanto alla presa era un numero che il giorno del cambio restava indietro —
    // una colonna che nasceva di una misura e, riportata «com'era», ne prendeva un'altra.
    const defaults = useMemo(
        () => Object.fromEntries(PANELS.map((panel) => [panel, getDefaultSize(panel, workspace)])) as Record<PanelKey, number>,
        [workspace]
    );

    const toggleCollapsed = useCallback((panel: PanelKey) => {
        setCollapsed((current) => {
            const next = current.includes(panel) ? current.filter((currentPanel) => currentPanel !== panel) : [...current, panel];
            // Le voci di prima della migrazione restano dove sono: chiudere un pannello non deve
            // portarsi via le scelte che il server non ha ancora confermato.
            writeLocal({ ...readLocal(), collapsed: next });
            return next;
        });
    }, []);

    return useMemo(
        () => ({ preferences, limits, defaults, setPreference, setPanelSize, resetPanelSizes, toggleCollapsed }),
        [defaults, limits, preferences, resetPanelSizes, setPanelSize, setPreference, toggleCollapsed]
    );
};

export const getWorkspaceRailWidth = (sectionRails: SectionRails) => RAIL_WIDTHS[sectionRails];

// Il Composer tiene le sue colonne allo stesso minimo (il massimo lo riceve da `limits`): lo legge da qui,
// così resta una definizione sola per tutte e due le stanze.
export const WORKSPACE_PANEL_CONTENT_MIN = MIN_CONTENT;
